
import { Component, Mixins, Prop } from "vue-property-decorator";
import { ConfirmModalModes } from "@/data/enums";
import { CreateElement } from "vue";
import { DataModules } from "@/store/modules/data";
import { IClient } from "@/models/clients";
import { IContractProduct } from "@/models/contracts";
import { IInvoice } from "@/models/invoices";
import { IModalComponent } from "@/models/modal";
import { ITemplateRecipientType } from "@/models/templates";
import { IUser } from "@/models/users";
import { UpmindObjectToDataModuleMapping } from "@/data/enums/objects";
import { UpmindObjectTypes } from "@/data/enums/objects";

import AdminBrandId from "@/mixins/adminBrandId";
import ClientMixin from "@/mixins/clientMixin";
import RecipientsField from "@/components/app/global/templates/templateFields/recipientsField.vue";
import TemplateChannelsMixins from "@/mixins/templatesChannelsMixin";
import TemplateMixin from "@/mixins/templateMixin";

@Component({
  components: {
    "client-search": () =>
      import("@/components/app/global/client/clientSearch.vue"),
    "user-search-table": () =>
      import("@/components/app/global/user/userSearchTable.vue")
  }
})
export default class SendTestEmailModal extends Mixins(AdminBrandId) {
  // Props

  @Prop({ required: true })
  public templateId!: string;

  @Prop({ default: null })
  public objectId!: string;

  @Prop({ required: true })
  public objectType!: UpmindObjectTypes;

  @Prop({ default: null })
  public recipientTypeId!: ITemplateRecipientType["id"];

  @Prop({ default: null })
  public recipientId!: string;

  // Data

  public isLoading = true;
  public isProcessing = false;
  public object: any = null;
  public client: IClient | null = null;
  public user: IUser | null = null;

  public templateMixin: TemplateMixin = new TemplateMixin({
    parent: this.$parent,
    propsData: {
      templateId: this.templateId,
      scope: `$sendTestEmail_${this.templateId}`
    }
  });

  public channelsMixin: TemplateChannelsMixins = new TemplateChannelsMixins({
    parent: this.$parent
  });

  public clientMixin: ClientMixin = new ClientMixin({
    parent: this.$parent
  });

  public userOnlyTypes = [UpmindObjectTypes.USER];

  public form: any = {};

  // Life-cycle methods

  public async created() {
    try {
      await Promise.all([
        this.templateMixin.loadData(),
        this.channelsMixin.getAll()
      ]);

      this.form = this.initForm();
    } finally {
      this.isLoading = false;
    }
  }

  public beforeDestroy() {
    this.clientMixin.$destroy();
    this.templateMixin.$destroy();
    this.channelsMixin.$destroy();
  }

  // Computed

  public get isClientRecipient() {
    return this.selectedRecipient?.code === "client";
  }

  public get isUserRecipient() {
    return this.selectedRecipient?.code === "user";
  }

  public get selectedRecipient() {
    return this.availableRecipientTypes?.find(
      i => i.code === this.form.recipient_type
    );
  }

  public get availableRecipientTypes() {
    return this.$_.uniqBy(
      this.channelsMixin.allRecipientTypes.filter(i =>
        this.userOnlyTypes.includes(this.objectType)
          ? ["user"].includes(i.code)
          : [/** "brand" */ "client", "user"].includes(i.code)
      ),
      "id"
    );
  }

  public get objectName() {
    switch (UpmindObjectToDataModuleMapping[this.objectType]) {
      case DataModules.INVOICES:
        return (this.object as IInvoice)?.number;
      case DataModules.CLIENTS:
        return this.clientMixin
          .getClientName({
            client: this.object as IClient
          })
          ?.toString();
      case DataModules.CONTRACTS_PRODUCTS:
        return (this.object as IContractProduct)?.product_name;
      default:
        return "";
    }
  }

  public get recipientEmail() {
    return this.isClientRecipient
      ? this.client?.email
      : this.isUserRecipient
      ? this.user?.email
      : null;
  }

  // Methods

  public initForm() {
    return {
      recipient_type: this.availableRecipientTypes?.find(
        i => i.id === this.recipientTypeId
      )?.code,
      recipient_id: this.recipientTypeId
    };
  }

  public async confirmSend() {
    const templateName = this.templateMixin?.template?.name;
    const recipientEmail = this.recipientEmail;

    const objectName = this.objectName;
    // eslint-disable-next-line no-async-promise-executor
    return new Promise<void>(async resolve => {
      const confirm = await this.$store.dispatch("ui/open/confirmModal", {
        config: {
          props: {
            autoClose: false,
            type: "is-success",
            mode: ConfirmModalModes.BUTTON,
            confirmButtonText: this.$t("_sentence.templates.send_test_email"),
            message: {
              render(h: CreateElement) {
                return h("div", {}, [
                  h(
                    "i18n",
                    {
                      props: {
                        path: "_sentence.templates.send_test_email_desc"
                      }
                    },
                    [
                      h("strong", { slot: "template" }, templateName),
                      h("strong", { slot: "recipient" }, recipientEmail),
                      h("strong", { slot: "object" }, objectName)
                    ]
                  )
                ]);
              }
            }
          },
          events: {
            confirmed: async () => {
              await this.send();
              confirm.close();
              resolve();
            }
          }
        }
      });
    });
  }

  public onClientSelect(client: IClient) {
    this.client = client;
    this.form.recipient_id = client?.id;

    /**
     * In case when category is "client" we shortcut selection as it will be filtered by client id so we skip that part and step to confirmation
     */
    if (
      DataModules.CLIENTS === UpmindObjectToDataModuleMapping[this.objectType]
    ) {
      this.object = client;
      return this.confirmSend();
    }

    // this.confirmSend({ email: client.email });
    this.openSelectObjectModal({ client });
  }

  public onUserSelect(user: IUser) {
    this.user = user;
    this.form.recipient_id = user?.id;

    /**
     * In case when category is "user" we shortcut selection
     */
    if (
      DataModules.USERS === UpmindObjectToDataModuleMapping[this.objectType]
    ) {
      this.object = user;
      return this.confirmSend();
    }

    this.openSelectObjectModal({ user });
  }

  public async openSelectObjectModal({ client, user }: any) {
    const modal = (await this.$store.dispatch(
      `data/${DataModules.TEMPLATES}/openSelectSendObjectModal`,
      {
        props: {
          template: this.templateMixin.template,
          objectType: this.objectType,
          client,
          user
        },
        events: {
          success: async ({ object }) => {
            this.object = object;
            await this.confirmSend();
            modal.close();
          }
        }
      }
    )) as IModalComponent;
  }

  public async send() {
    try {
      this.isProcessing = true;

      await this.$store.dispatch(`data/${DataModules.TEMPLATES}/send`, {
        id: this.templateId,
        data: {
          object_id: this.objectId || this.object?.id,
          recipient_id: this.recipientId || this.form.recipient_id,
          recipient_type: this.selectedRecipient?.code
        }
      });

      this.$store.dispatch("toast/show", {
        message: this.$t("_sentence.templates.test_email_sent")
      });
    } finally {
      this.isProcessing = false;
      this.$emit("close");
    }
  }

  // Render

  public renderRecipientsField(h: CreateElement) {
    return h(RecipientsField, {
      attrs: { value: this.form.recipient_type },
      on: { input: e => (this.form.recipient_type = e) },
      props: {
        recipientTypes: this.availableRecipientTypes,
        withDescription: false,
        field: "code"
      },
      class: "has-margin-bottom-100"
    });
  }

  public renderClientSearch(h: CreateElement) {
    return h("client-search", {
      props: {
        brandId: this.brandId,
        limit: 8,
        onSelect: this.onClientSelect,
        showGuestsToggle: false
      }
    });
  }

  public renderUserSearchTable(h: CreateElement) {
    return h("user-search-table", {
      props: {
        excludeInactive: false,
        rowTag: "tr",
        rowProps: () => ({}),
        rowEvents: user => ({
          click: () => this.onUserSelect(user)
        })
      }
    });
  }

  public render(h: CreateElement) {
    return h(
      "modal-card",
      {
        props: { title: this.$t("_sentence.templates.send_test_email") },
        on: { close: () => this.$emit("close") }
      },
      [
        h("is-loading", { props: { if: this.isLoading } }, [
          this.renderRecipientsField(h),
          this.isClientRecipient
            ? this.renderClientSearch(h)
            : this.isUserRecipient
            ? this.renderUserSearchTable(h)
            : null
        ])
      ]
    );
  }
}
